/**
 * ValidationBanner
 *
 * Compact inline indicator + popover for validation issues.
 * Lives in the header bar, shows a small chip with issue count
 * that opens a dropdown with details and actionable CTAs.
 */

import {
  DocumentTextIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { Transmission } from '@/services/api/correspondenceService';

import type {
  CorrespondenceComponentSchema,
  PackageValidationStatus,
} from '../types';
import { getComponentTitle } from '../utils/component';
import {
  analyzeMissingFields,
  getCategoryLabel,
  type MissingFieldAnalysis,
} from '../utils/missingFieldAnalysis';

// ============================================================================
// Types
// ============================================================================

interface ValidationBannerProps {
  validationStatus: PackageValidationStatus;
  components: CorrespondenceComponentSchema[] | undefined;
  isLoading?: boolean;
  className?: string;
  transmission?: Partial<Transmission> | null;
  recipientName?: string | null;
  onTransmissionAction?: (
    action: 'selectRecipient' | 'setTransmission',
  ) => void;
  onClientInfoAction?: () => void;
}

interface IssueGroup {
  componentId: string;
  componentTitle: string;
  fields: { fieldId: string; fieldName: string }[];
}

// ============================================================================
// Helpers
// ============================================================================

function groupMissingFieldsByComponent(
  validationStatus: PackageValidationStatus,
  components: CorrespondenceComponentSchema[] | undefined,
): IssueGroup[] {
  if (!components) return [];

  return validationStatus.componentResults
    .filter((result) => result.missingCount > 0)
    .map((result) => {
      const component = components.find((c) => c.id === result.componentId);
      return {
        componentId: result.componentId,
        componentTitle: component
          ? getComponentTitle(component)
          : 'Unknown Document',
        fields: Object.values(result.missingFields).map((f) => ({
          fieldId: f.fieldId,
          fieldName: f.fieldName,
        })),
      };
    });
}

function getTitle(analysis: MissingFieldAnalysis): string {
  const { issueCount, needsTransmission, needsClientInfo, transmissionCTA } =
    analysis;

  if (issueCount > 1) {
    return `${issueCount} items need attention`;
  }
  if (needsTransmission && transmissionCTA) {
    return transmissionCTA.text;
  }
  if (needsClientInfo) {
    return 'Client info incomplete';
  }
  return `${analysis.totalMissingFields} missing field${analysis.totalMissingFields !== 1 ? 's' : ''}`;
}

// ============================================================================
// Main Component - Compact popover-based validation
// ============================================================================

export const ValidationBanner: React.FC<ValidationBannerProps> = ({
  validationStatus,
  components,
  isLoading = false,
  className,
  transmission,
  recipientName,
  onTransmissionAction,
  onClientInfoAction,
}) => {
  const [open, setOpen] = useState(false);

  const { hasMissingFields, missingFieldsCount } = validationStatus;

  const analysis = analyzeMissingFields(
    validationStatus,
    recipientName,
    transmission ?? null,
  );
  const {
    needsTransmission,
    needsClientInfo,
    transmissionCTA,
    categorizedFields,
  } = analysis;

  if (isLoading) {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-zinc-50 border border-zinc-200 text-[11px] text-zinc-500',
          className,
        )}
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        <span>Validating</span>
      </div>
    );
  }

  const hasAnyIssues = hasMissingFields || needsTransmission;
  if (!hasAnyIssues || (missingFieldsCount === 0 && !needsTransmission)) {
    return null;
  }

  const issueGroups = groupMissingFieldsByComponent(
    validationStatus,
    components,
  );
  const title = getTitle(analysis);
  const totalCount =
    missingFieldsCount + (needsTransmission ? 1 : 0) + (needsClientInfo ? 1 : 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1 px-2 py-1 rounded-md',
            'bg-amber-50 text-amber-700 text-[11px] font-medium',
            'hover:bg-amber-100 transition-colors',
            'border border-amber-200/60',
            className,
          )}
        >
          <ExclamationTriangleIcon className="h-3 w-3" />
          <span>{totalCount}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-80 p-0 rounded-xl border border-amber-200/60 shadow-lg overflow-hidden"
      >
        {/* Header */}
        <div className="px-3 py-2.5 bg-amber-50/80 border-b border-amber-200/40">
          <p className="text-xs font-semibold text-amber-950">{title}</p>
        </div>

        {/* CTAs */}
        {(needsTransmission || needsClientInfo) && (
          <div className="px-3 py-2 flex flex-wrap gap-1.5 border-b border-amber-100">
            {needsTransmission &&
              transmissionCTA &&
              onTransmissionAction && (
                <Button
                  onClick={() => {
                    onTransmissionAction(transmissionCTA.action);
                    setOpen(false);
                  }}
                  color="amber"
                  className="text-[11px] font-medium h-7 px-2.5"
                >
                  {transmissionCTA.text}
                </Button>
              )}
            {needsClientInfo && onClientInfoAction && (
              <Button
                onClick={() => {
                  onClientInfoAction();
                  setOpen(false);
                }}
                color="amber"
                className="text-[11px] font-medium h-7 px-2.5"
              >
                Edit Client Info
              </Button>
            )}
          </div>
        )}

        {/* Category pills */}
        {categorizedFields.length > 1 && (
          <div className="px-3 py-2 border-b border-amber-100">
            <div className="flex flex-wrap gap-1">
              {categorizedFields.map((cat) => (
                <span
                  key={cat.category}
                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100/80 text-[10px] text-amber-800"
                >
                  {getCategoryLabel(cat.category)}: {cat.count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Field breakdown */}
        {issueGroups.length > 0 && (
          <div className="px-3 py-2.5 max-h-48 overflow-y-auto space-y-2">
            {issueGroups.map((group) => (
              <div key={group.componentId}>
                <div className="flex items-center gap-1 mb-1">
                  <DocumentTextIcon className="h-3 w-3 text-amber-500" />
                  <span className="text-[11px] font-medium text-zinc-700 truncate">
                    {group.componentTitle}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1 pl-4">
                  {group.fields.map((field) => (
                    <span
                      key={field.fieldId}
                      className="px-1.5 py-0.5 rounded bg-amber-50 text-[10px] text-amber-700 border border-amber-200/50"
                    >
                      {field.fieldName}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// ============================================================================
// Compact Indicator (for standalone use)
// ============================================================================

export const ValidationIndicator: React.FC<{
  count: number;
  onClick?: () => void;
  className?: string;
}> = ({ count, onClick, className }) => {
  if (count === 0) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-md',
        'bg-amber-50 text-amber-700 text-xs font-medium',
        'hover:bg-amber-100 transition-colors',
        'border border-amber-200/50',
        className,
      )}
    >
      <ExclamationTriangleIcon className="h-3 w-3" />
      {count}
    </button>
  );
};
